/* External dependencies */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';

/* Internal dependencies */
import Colors from '../colors';
import Icon, { Icons } from '../icon/Icon';
import { ApplicationState } from '../store';
import { CurrentUserState, getCurrentUser } from '../store/ducks/currentUser';
import ModalButton from 'src/modal/ModalButton';
import Button from 'src/button/Button';
import { formatNumber } from 'src/common/helpers/numbers';
import { getProfile } from 'src/store/ducks/currentProfile';
import { ProfileType } from 'src/types/Profile';

type OwnProps = {
  profile: ProfileType;
  render?: (onPress: Function) => React.ReactNode;
  disabled?: boolean;
};

type StateProps = {
  currentUser: CurrentUserState['user'];
};

type Props = OwnProps & StateProps;

const Beatscore: React.FC<Props> = ({ currentUser, profile, render, disabled = false }) => {
  const { id, name, type, score = 0 } = profile;
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const currentUserId = get(currentUser, 'id');
  let defaultDescription = 'Beatscore measures how active you are in the Beatmatch community. Add friends, share plans, create and join events, and post more to increase your Beatscore. Be more social.';

  if (type === 'community') {
    defaultDescription = 'Beatscore shows how active your community is on Beatmatch. Grow your score by hosting events, posting updates, and getting members to join and engage. Create a movement.';
  } else if (type === 'artist') {
    defaultDescription = 'Beatscore measures how active you are as an artist on Beatmatch. Share updates, promote gigs, and grow your fanbase to boost your Beatscore. Make your presence felt.';
  } else if (type === 'venue') {
    defaultDescription = 'Beatscore measures how active your venue is on Beatmatch. Host events, connect with loyal customers, and become the go-to spot to boost your Beatscore. Make your space the place.';
  }

  if (!score) return null;

  const openModal = () => { setDrawerOpen(true); };
  const closeModal = () => { setDrawerOpen(false); };

  return (
    <ModalButton
      open={drawerOpen}
      openModal={openModal}
      closeModal={closeModal}
      modalProps={{
        className: 'bm-Modal--mobile text-white',
        contentClassName: 'bg-secondaryButton',
      }}
      content={(
        <div className="d-flex flex-column justify-content-center align-items-center pt-4 pb-4">
          <div
            className="bg-secondaryButton d-flex justify-content-center align-items-center mb-4"
            style={{
              width: 75,
              aspectRatio: '1',
              borderRadius: 50,
            }}
          >
            <Icon name={Icons.lightning} className="bm-Icon--primary" size={28} solid />
          </div>
          <h4 className="text-bold text-center mb-4">
            {id === currentUserId ? 'Your' : `${name}'s`} Beatscore
          </h4>
          <div className="mb-4">
            <h2 className="bm-Text--primary text-bold text-center">
              {score}
            </h2>
          </div>
          <p className="text-center">
            {`${id === currentUserId ? '' : `${name} has a Beatscore of ${score}!\n`} ${defaultDescription}`}
          </p>
        </div>
      )}
      render={Boolean(render) ? render : (onClick) => (
        <Button
          disabled={disabled}
          onClick={onClick}
          style={{
            flexDirection: 'row',
            backgroundColor: 'transparent',
            border: 'none',
            opacity: 1,
          }}
        >
          <div
            className="d-flex align-items-center bg-primary pl-3 pr-3 pt-1 pb-1"
            style={{
              backgroundColor: Colors.primary, borderRadius: 50,
              borderWidth: 0, borderColor: Colors.inactiveTab,
            }}
          >
            <Icon name={Icons.lightning} className="bm-Icon--white mr-2" size={16} solid />
            <h6 className="text-bold text-white m-0 p-0">{formatNumber(score)}</h6>
          </div>
        </Button>
      )}
    />
  );
};

const mapStateToProps = (state: ApplicationState, { profile }: OwnProps) => {
  const profileId = get(profile, 'id');

  return {
    currentUser: getCurrentUser(state),
    profile: getProfile(state, profileId) || profile,
  };
};

export default connect(mapStateToProps)(Beatscore);